import { Button } from '@flywire/headlessui-tailwindcss';
import { HTTPStatuses, type HttpError } from '@packages/domain/constants/http';
import { logIn } from 'actions/authentication/authentication';
import { trackUserLoginSignup } from 'actions/tracker/tracker';
import { setKeepSession } from 'actions/ui/authentication/authentication';
import { newNotification } from 'actions/ui/ui';
import { Alert } from 'components/Alert/Alert';
import { Checkbox } from 'components/Checkbox/Checkbox';
import { Fetching } from 'components/Fetching/Fetching';
import { Heading } from 'components/Heading/Heading';
import { TextInput } from 'components/input/TextInput/TextInput';
import { RequiredFieldLabel } from 'components/RequiredFieldLabel/RequiredFieldLabel';
import { SocialSignIn } from 'components/SocialSignIn/SocialSignIn';
import {
  Captcha,
  type CaptchaRef,
} from 'components/thirdParty/Captcha/Captcha';
import { SENDER_EMAIL_FIELD } from 'constants/fields';
import {
  UserLoginSource,
  UserLoginType,
} from 'contexts/LoginContext/LoginContext';
import { useLoginOrigin } from 'hooks/useLoginOrigin/useLoginOrigin';
import type { FormEvent, MutableRefObject, ReactNode } from 'react';
import { useRef, useState } from 'react';
import { useSelector } from 'react-redux';
import type { RootState } from 'reducers/types';
import { getKeepSession } from 'selectors/ui/authentication/authentication';
import { getFieldValue } from 'selectors/ui/ui';
import { useDispatch } from 'store/configureStore';
import { isHttpErrorWithParsedBody } from 'utils/errors/errors';
import { useTranslations } from 'utils/translations/useTranslations';
import './UserLogin.scss';

const LOCKED_USER = 'locked_user';
const USER_NEEDS_TO_CHANGE_PASSWORD = 'user_needs_to_change_password';
const INVALID_CREDENTIALS = 'invalid_credentials';
const WAIT_TIME = 75;

export type UserLoginProps = {
  firstInputRef?: MutableRefObject<HTMLInputElement | null>;
  handleSignUpClick: () => void;
  onLoginSuccess?: () => void;
  resetPassword: (showAlert?: boolean) => void;
  header?: ReactNode;
  hideSocialLogin?: boolean;
};

type Error = { error: string; message?: string };
type ErrorBody = { parsedBody?: { errors?: Error[] } };
type FormValues = {
  username: string;
  password: string;
  error?: string | boolean;
};

const UserLogin = ({
  firstInputRef,
  handleSignUpClick,
  onLoginSuccess,
  resetPassword,
  header,
  hideSocialLogin,
}: UserLoginProps) => {
  const i18n = useTranslations();
  const dispatch = useDispatch();
  const captchaRef = useRef<CaptchaRef>(null);
  const shouldKeepSession = useSelector(getKeepSession);
  const senderEmail = useSelector((state: RootState) =>
    getFieldValue(state, SENDER_EMAIL_FIELD),
  );
  const [formValues, setFormValues] = useState<FormValues>({
    username: senderEmail ? (senderEmail as string) : '',
    password: '',
  });
  const { loginOrigin } = useLoginOrigin();

  const loginSuccess = () => {
    dispatch(
      newNotification({
        message: 'notifications.login_user_success',
      }),
    );
    onLoginSuccess?.();
    resetCaptcha();
  };

  const handleSocialSignInError = (err: unknown) => {
    resetCaptcha();

    if (isHttpErrorWithParsedBody(err)) {
      const { status, parsedBody } = err as HttpError & ErrorBody;
      const { errors = [] } = parsedBody || {};
      const isLockedUser = errors.some(
        (item: Error) => item.error === LOCKED_USER,
      );

      if (status === HTTPStatuses.UNAUTHORIZED) {
        let message = 'notifications.social_signin_unauthorized';
        if (isLockedUser) {
          message = 'userLogin.alert.locked_user';
        }

        dispatch(
          newNotification({
            message,
            type: 'error',
          }),
        );
      }
    }
  };

  const handleSubmit = (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault();

    if (captchaRef.current?.isEnabled()) {
      captchaRef.current?.execute();
    } else {
      handleLogin();
    }
  };

  const resetCaptcha = () => {
    captchaRef.current?.reset();
  };

  const handleLogin = async (captchaToken?: string) => {
    const { username, password } = formValues;

    setFormValues({ ...formValues, error: undefined });

    try {
      await dispatch(logIn({ username, password, captchaToken }));
      loginSuccess();
      dispatch(
        trackUserLoginSignup(
          UserLoginType.LOGIN,
          UserLoginSource.EMAIL,
          loginOrigin,
        ),
      );
    } catch (error) {
      if (isHttpErrorWithParsedBody(error)) {
        const { parsedBody: { errors = [] } = {} } = error as ErrorBody;
        const userNeedsToChangePassword = errors.find(
          (item: Error) => item.error === USER_NEEDS_TO_CHANGE_PASSWORD,
        );

        const showUpdatePasswordAlert = true;
        if (userNeedsToChangePassword) {
          return resetPassword(showUpdatePasswordAlert);
        }

        const code = errors.find((item: Error) => item.error === LOCKED_USER);

        onError(code);
      } else {
        onError();
      }
    }
  };

  const onError = (code?: { error: string }) => {
    setFormValues({
      ...formValues,
      error: code ? code.error : INVALID_CREDENTIALS,
    });
    setTimeout(() => {
      firstInputRef?.current?.focus();
    }, WAIT_TIME);

    resetCaptcha();
  };

  const handleChangeEmail = (_: string, value = '') => {
    setFormValues({ ...formValues, username: value });
  };

  const handleChangePassword = (_: string, value = '') => {
    setFormValues({ ...formValues, password: value });
  };

  const handleKeepSession = () => {
    dispatch(setKeepSession(!shouldKeepSession));
  };

  const handleLoginWithCaptcha = async (token: string) => {
    if (token) {
      handleLogin(token);
    } else {
      onError();
    }
  };

  const { error, username, password } = formValues;

  return (
    <>
      <Fetching entity="authentication" />

      {header ? (
        header
      ) : (
        <Heading
          as="h2"
          size="large"
          className="textAlign-center marginBottom-xxl"
          id="Page-title"
        >
          {i18n.t('userLogin.title')}
        </Heading>
      )}

      {!hideSocialLogin && (
        <SocialSignIn
          onSuccess={loginSuccess}
          onError={handleSocialSignInError}
          isLogin={true}
        />
      )}

      <form data-testid="logInForm" onSubmit={handleSubmit} noValidate>
        <div aria-live="assertive" role="status">
          {error && (
            <Alert
              type="danger"
              id="userLogin-error-msg"
              aria-live="assertive"
              role="status"
            >
              {i18n.t(`userLogin.alert.${error}`)}
            </Alert>
          )}
        </div>
        <RequiredFieldLabel />
        <div className="flex flex-col gap-8">
          <TextInput
            aria-describedby={error ? 'userLogin-error-msg' : undefined}
            autoFocus
            required
            error={!!error}
            data-testid="logInEmail"
            floatingLabel
            forwardRef={firstInputRef}
            label={i18n.t('userLogin.username')}
            name="username"
            onChange={handleChangeEmail}
            value={username}
          />
          <TextInput
            aria-describedby={error ? 'userLogin-error-msg' : undefined}
            required
            error={!!error}
            data-testid="logInPassword"
            floatingLabel
            label={i18n.t('userLogin.password')}
            name="logInPassword"
            onChange={handleChangePassword}
            type="password"
            value={password}
          />
        </div>
        <div className="RecoverPassword">
          <button
            className="RecoverPassword-button buttonLikeLink"
            onClick={() => resetPassword()}
            data-testid="recoverPassword"
            type="button"
            role="link"
          >
            {i18n.t('userLogin.recoverPassword')}
          </button>
        </div>
        <div className="paddingTop-lg">
          <Checkbox
            name="keep-session-open"
            label={i18n.t('userLogin.keepSession')}
            checked={shouldKeepSession}
            onChange={handleKeepSession}
            className="UserLogin-keepSession"
          />
        </div>
        <Button
          tone="primary"
          size="full"
          type="submit"
          className="mt-10"
          data-testid="logInButton"
        >
          {i18n.t('userLogin.button.login')}
        </Button>
      </form>
      <div className="UserLogin-question">{i18n.t('userLogin.question')}</div>
      <div className="UserLogin-link">
        <button
          onClick={handleSignUpClick}
          type="button"
          role="link"
          data-testid="signup"
        >
          {i18n.t('userLogin.signup.link')}
        </button>
      </div>
      <Captcha
        ref={captchaRef}
        onVerify={handleLoginWithCaptcha}
        onClose={resetCaptcha}
        onError={onError}
      />
    </>
  );
};

export { UserLogin };
